import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  useTable,
  useSortBy,
  Column,
  useFilters,
  useGlobalFilter,
  TableInstance,
  TableState,
  UseGlobalFiltersInstanceProps,
  UseGlobalFiltersState,
} from 'react-table';
import { ReactComponent as ArrowUp } from './../../../../images/arrow-narrow-up.svg';
import { ReactComponent as ArrowDown } from './../../../../images/arrow-narrow-down.svg';
import ReactDOMServer from 'react-dom/server';
import { createMultiConditionFilter } from '../../../../utilities/tableFilters';
import { DefaultColumnFilter } from '../../../../components/data-tables/helpers/DefaultColumnFilter';
import TableLegend from '../../../../components/data-tables/TableLegend';
import { TableDataItem } from '../../../../types';
import { ColumnData, FixedColumnData } from '../../RealtimeInsightsSlice';
import WellName from '../../../../components/common/table-cell/well-name';
import WellStatus from '../../../../components/common/table-cell/well-status';
import Alarm from '../../../../components/common/table-cell/alarm';
import { useFixedHeaderWidths } from './useFixedHeaderWidths';
import './style.scss';

// Helper function to flatten nested data
const flattenRowData = (row: any): any => {
  const flattened = { ...row };

  if (row.wellDetails) {
    Object.keys(row.wellDetails).forEach((key) => {
      flattened[`wellDetails.${key}`] = row.wellDetails[key];
    });
  }

  return flattened;
};

interface DataTableProps {
  ref: any;
  visibleColumns: string[];
  onUpdateVisibleColumns: (columns: string[]) => void;
  searchTerm?: string;
  applyTrigger: boolean;
  isResetClicked?: boolean;
  installNumber: number | null;
  onFilteredDataChange?: (filteredData: any[]) => void;
  columnObjects: ColumnData[];
  fixedColumnObjects: FixedColumnData[];
  allRows: any[];
}

// Extend TableInstance and TableState types to include global filter properties
interface ExtendedTableInstance<T extends object> extends TableInstance<T>, UseGlobalFiltersInstanceProps<T> {}
interface ExtendedTableState<T extends object> extends TableState<T>, UseGlobalFiltersState<T> {}

const HeaderCell: React.FC<{ label: string }> = ({ label }) => {
  return (
    <div className='flex gap-[4px] items-center header-cell'>
      <span className='text-[12px] font-semibold text-[#f7f9f9] text-'>{label}</span>
    </div>
  );
};

// Define a default UI for filtering
const DataTable: React.FC<DataTableProps> = React.forwardRef<HTMLDivElement, DataTableProps>((props, ref) => {
  const {
    visibleColumns,
    applyTrigger,
    isResetClicked,
    installNumber,
    onFilteredDataChange,
    columnObjects,
    fixedColumnObjects,
    allRows,
  } = props;

  const [appliedVisibleColumns, setAppliedVisibleColumns] = useState<string[]>([]);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);

  const previousFilteredDataRef = React.useRef<any[]>([]);

  const filteredTableData = useMemo(() => {
    if (!allRows || allRows?.length < 0) return [];
    let filtered = [...allRows];

    if (installNumber !== null) {
      filtered = filtered.filter((row) => row.InstallNumber === installNumber);
    }

    return filtered;
  }, [allRows, installNumber, visibleColumns]);

  const data = useMemo(() => {
    const mappedData = allRows?.map((item: any) => {
      return flattenRowData(item);
    });
    return mappedData;
  }, [allRows]);

  const [tableRows, setTableRows] = useState(data ?? []);
  const [filteredData, setFilteredData] = useState(tableRows);

  const dataRef = useRef(data);
  const currentFiltersRef = useRef(currentFilters);
  const tableRowsRef = useRef(tableRows);
  const fixedColumnsDefs = fixedColumnObjects.map((item) => ({
    accessor: item.key,
    label: item.label,
  }));
  const fixedColumnsKeys = fixedColumnObjects.map((item) => item.key);
  const { getHeaderRef, widths } = useFixedHeaderWidths(fixedColumnsKeys);

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    tableRowsRef.current = tableRows;
  }, [tableRows]);

  const arraysAreEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  useEffect(() => {
    if (onFilteredDataChange && JSON.stringify(previousFilteredDataRef.current) !== JSON.stringify(filteredTableData)) {
      previousFilteredDataRef.current = filteredTableData;
      onFilteredDataChange(filteredTableData);
    }
  }, [filteredTableData, onFilteredDataChange]);

  useEffect(() => {
    if (allRows) {
      if (JSON.stringify(data) !== JSON.stringify(tableRows)) {
        setTableRows(data);
      }
    }
  }, [data, visibleColumns]);

  useEffect(() => {
    if (applyTrigger && !arraysAreEqual(visibleColumns, appliedVisibleColumns)) {
      setAppliedVisibleColumns(visibleColumns);
    }
  }, [applyTrigger, visibleColumns]);

  useEffect(() => {
    if (isResetClicked) {
      setAppliedVisibleColumns(visibleColumns);
    }
  }, [isResetClicked]);

  useEffect(() => {
    if (columnObjects?.length && !applyTrigger) {
      setAppliedVisibleColumns([...visibleColumns]);
    }
  }, [columnObjects]);

  const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef), []);

  // Function to handle row click for selection
  const handleRowClick = (rowIndex: number) => {
    setFilteredData((prev: any) =>
      prev.map((row: any, index: any) => ({
        ...row,
        IsHighlighted: index === rowIndex ? true : false,
      })),
    );
  };

  const mergedColumns: ColumnData[] = [
    ...(Array.isArray(columnObjects) ? columnObjects : []),
    ...((Array.isArray(fixedColumnObjects) ? fixedColumnObjects : []) as any),
  ].sort((a, b) => a.order - b.order);

  const columnDefs: { accessor: keyof TableDataItem; label: string }[] = mergedColumns
    .filter((col: ColumnData) => col.key)
    .map((col: ColumnData) => ({
      accessor: col.key as keyof TableDataItem,
      label: col.label,
    }));

  const updatedColumnDefs = [
    ...fixedColumnsDefs,
    ...columnDefs.filter((column) => !fixedColumnsDefs.some((fixedCol: any) => fixedCol.accessor === column.accessor)),
  ];

  const getNestedValue = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];
    return path.split('.').reduce((acc: any, key: string) => (acc != null ? acc[key] : undefined), obj);
  };

  const columns: Column<TableDataItem>[] = useMemo(() => {
    return updatedColumnDefs.map(({ accessor, label }: any) => {
      const accessorString = String(accessor || '');
      const isNested = accessorString.includes('.');
      const accessorFn: any = isNested ? (row: any) => getNestedValue(row, accessorString) : accessorString;

      const leafKey = accessorString.includes('.') ? accessorString.split('.').slice(-1)[0] : accessorString;

      if (leafKey === 'wellName') {
        return {
          id: accessorString,
          accessor: accessorFn,
          Header: () => <HeaderCell label={label} />,
          filter: multiConditionFilter,
          Cell: ({ value }: { value: string }) => <WellName value={value} />,
        } as Column<TableDataItem>;
      } else if (leafKey === 'status') {
        return {
          id: accessorString,
          accessor: accessorFn,
          Header: () => <HeaderCell label={label} />,
          filter: multiConditionFilter,
          Cell: ({ value }: { value: string }) => <WellStatus value={value} />,
        } as Column<TableDataItem>;
      } else if (leafKey === 'alarms') {
        return {
          id: accessorString,
          accessor: accessorFn,
          Header: () => <HeaderCell label={label} />,
          filter: multiConditionFilter,
          Cell: ({ value }: { value: string }) => <Alarm value={value} />,
        } as Column<TableDataItem>;
      } else {
        return {
          id: accessorString,
          accessor: accessorFn,
          Header: () => <HeaderCell label={label} />,
          filter: multiConditionFilter,
          Cell: ({ value }: { value: any }) =>
            value === 0 || value === '-' ? (
              <span className='text-center'>-</span>
            ) : (
              <span className='text-center'>{typeof value === 'number' ? value.toFixed(2) : value}</span>
            ),
        } as Column<TableDataItem>;
      }
    });
  }, [updatedColumnDefs, multiConditionFilter]);

  const visibleColumnsData = useMemo(() => {
    const fixed = columns.filter((column) => fixedColumnsKeys.includes((column.id || column.accessor) as string));

    const scrollable = columns
      .filter((column) => {
        const colId = (column.id || column.accessor) as string;
        return !fixedColumnsKeys.includes(colId) && appliedVisibleColumns.includes(colId);
      })
      .sort((a, b) => {
        const aId = (a.id || a.accessor) as string;
        const bId = (b.id || b.accessor) as string;
        return appliedVisibleColumns.indexOf(aId) - appliedVisibleColumns.indexOf(bId);
      });
    return [...fixed, ...scrollable];
  }, [appliedVisibleColumns]);

  const defaultColumn: any = React.useMemo(
    () => ({
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  useEffect(() => {
    if (JSON.stringify(filteredData) !== JSON.stringify(tableRows)) {
      setFilteredData(tableRows);
    }
  }, [tableRows, visibleColumns, filteredData]);

  const instance = useTable<TableDataItem>(
    {
      columns: visibleColumnsData,
      data: filteredData,
      defaultColumn: defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  ) as ExtendedTableInstance<TableDataItem> & { state: ExtendedTableState<TableDataItem> };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state, setGlobalFilter, setAllFilters } =
    instance as ExtendedTableInstance<TableDataItem> & {
      state: ExtendedTableState<TableDataItem>;
      setGlobalFilter: (filterValue: string | undefined) => void;
      setAllFilters: (filters: any[]) => void;
    };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      const mappedFilters = remainingFilters.map((filter) => ({
        id: filter.columnId,
        value: filter.filterValue,
      }));
      setAllFilters(mappedFilters);
    }
  };
  const { globalFilter } = state;
  useEffect(() => {
    const htmlString = ReactDOMServer.renderToStaticMarkup(
      <div className='flex pt-[7px] pr-[12px] pb-[6px] pl-[12px] gap-[8px] items-center self-stretch shrink-0 flex-nowrap bg-[#001023] rounded-[8px] border-solid border border-[#4a5463] relative shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] search-input w-full'>
        <span className='ico-search' />
        <div className='flex w-full items-center shrink-0 flex-nowrap relative justify-start custom-view-search'>
          <input
            type='text'
            value={globalFilter || ''}
            onChange={(e) => {
              setGlobalFilter(e.target.value || undefined);
            }}
            placeholder='Search...'
            className="flex w-full h-[24px] justify-start items-start shrink-0 basis-auto font-['Mulish'] text-[16px] font-normal leading-[24px] text-[#b8c5cc] relative text-left whitespace-nowrap bg-transparent border-none"
          />
        </div>
        <button
          id='realtime-insights-clear-search'
          className='ml-[8px] text-[#b8c5cc] hover:text-white text-[16px] leading-none p-0 m-0 bg-transparent border-none cursor-pointer flex items-center justify-center hidden'
          type='button'
          aria-label='Clear search'
        >
          ✕
        </button>
      </div>,
    );
    if (ref && 'current' in ref && ref.current) {
      ref.current.innerHTML = htmlString; // Updating the HTML content
      ref.current.onkeyup = (e: Event) => {
        const inputEvent = e as unknown as React.ChangeEvent<HTMLInputElement>;
        setGlobalFilter(inputEvent.target.value || undefined);
        document.getElementById('realtime-insights-clear-search')!.style.display = inputEvent.target.value
          ? 'flex'
          : 'none';
        document.getElementById('realtime-insights-clear-search')!.onclick = () => {
          setGlobalFilter(undefined);
          const inputElement = ref.current?.querySelector('input[type="text"]') as HTMLInputElement;
          if (inputElement) {
            inputElement.value = '';
          }
          document.getElementById('realtime-insights-clear-search')!.style.display = 'none';
        };
      };
    }
  }, []);

  const [offsets, setOffsets] = useState<Record<string, number>>({});

  useEffect(() => {
    const newOffsets = fixedColumnsKeys.reduce(
      (acc, id, i) => {
        const prevId = fixedColumnsKeys[i - 1];
        acc[id] = prevId ? (acc[prevId] ?? 0) + (widths[prevId] ?? 0) : 0;
        return acc;
      },
      {} as Record<string, number>,
    );
    setOffsets(newOffsets);
  }, [widths, fixedColumnsKeys]);

  if (!Array.isArray(allRows)) {
    return null;
  }

  return (
    <section className='table-container-wrapper'>
      <div className={`table-container realtime-insights-table-container ${rows.length === 0 ? 'empty-table' : ''}`}>
        <table {...getTableProps()} className='data-table'>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--background-color, #131f2e)' }}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => {
                  const isFixedColumn = fixedColumnsKeys.includes(column.id);
                  const isLastFixed = fixedColumnsKeys.indexOf(column.id) === fixedColumnsKeys.length - 1;
                  const style = isFixedColumn
                    ? {
                        left: offsets[column.id] ?? 0,
                        width: widths[column.id] ?? 'auto',
                        maxWidth: '200px',
                      }
                    : {};
                  return (
                    <th
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                      })}
                      style={style}
                      ref={isFixedColumn ? getHeaderRef(column.id) : null}
                      className={`h-[44px] pt-[16px] pr-[12px] pb-[16px] pl-[12px] border-solid border-t border-t-[#4a5463] text-left ${
                        isFixedColumn ? 'fixed-column' : ''
                      }  ${rows.length === 0 ? 'Icon-column-width' : ''}  ${column.id}-column`}
                    >
                      <div>{column.render('Header')}</div>
                      <div>{column.isSorted ? column.isSortedDesc ? <ArrowDown /> : <ArrowUp /> : ''}</div>

                      <div>{column.canFilter ? column.render('Filter') : null}</div>
                      {isLastFixed && <div className='fixed-column-divider'></div>}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className='table-scroll'>
            {rows.length > 0 &&
              rows.map((row, rowIndex) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={`cursor-pointer ${row.original.IsHighlighted ? 'highlighted-row' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(rowIndex);
                    }}
                  >
                    {row.cells.map((cell) => {
                      const isFixedColumn = fixedColumnsKeys.includes(cell.column.id);
                      const isLastFixed = fixedColumnsKeys.indexOf(cell.column.id) === fixedColumnsKeys.length - 1;
                      const style = isFixedColumn
                        ? {
                            left: offsets[cell.column.id] ?? 0,
                            width: widths[cell.column.id] ?? 'auto',
                            maxWidth: '200px',
                          }
                        : {};
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={`border-t border-t-[#4a5463] p-[12px] ${isFixedColumn ? 'fixed-column' : ''}   
                            `}
                          style={style}
                        >
                          {cell.render('Cell')}
                          {isLastFixed && <div className='fixed-column-divider'></div>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <div className='no-matching-records-container text-center p-4 pl-6 pr-6'>
          <span className='no-matching-records-message'>No matching records found</span>
        </div>
      )}

      <TableLegend />
    </section>
  );
});

export default DataTable;
